import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react'
import axios from 'axios'
import { api, setTokens, clearTokens, storedRefreshToken } from './api'

const BASE = (import.meta.env.VITE_API_URL as string) ?? ''

export interface User {
  id: string
  email: string
  emailVerified: boolean
  firstName: string | null
  lastName: string | null
  phone: string | null
  createdAt: string
}

interface TokenPair {
  accessToken: string
  refreshToken: string
}

export interface RegisterInput {
  email: string
  password: string
  firstName: string
  lastName: string
  phone?: string
}

interface AuthCtx {
  user: User | null
  loading: boolean
  login(email: string, password: string): Promise<void>
  googleAuth(credential: string): Promise<void>
  register(input: RegisterInput): Promise<void>
  verifyEmail(token: string): Promise<void>
  resendVerification(email: string): Promise<void>
  forgotPassword(email: string): Promise<void>
  resetPassword(token: string, password: string): Promise<void>
  logout(): Promise<void>
  refreshUser(): Promise<void>
}

const Ctx = createContext<AuthCtx | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const rt = storedRefreshToken()
    if (!rt) {
      setLoading(false)
      return
    }

    axios
      .post<TokenPair>(`${BASE}/auth/refresh`, { refreshToken: rt })
      .then(({ data }) => {
        setTokens(data.accessToken, data.refreshToken)
        return api.get<User>('/auth/me')
      })
      .then(({ data }) => setUser(data))
      .catch(() => clearTokens())
      .finally(() => setLoading(false))
  }, [])

  async function loadMe() {
    const { data } = await api.get<User>('/auth/me')
    setUser(data)
  }

  async function login(email: string, password: string) {
    const { data } = await api.post<TokenPair>('/auth/login', {
      email,
      password,
    })
    setTokens(data.accessToken, data.refreshToken)
    await loadMe()
  }

  async function googleAuth(credential: string) {
    const { data } = await api.post<TokenPair>('/auth/google', {
      idToken: credential,
    })
    setTokens(data.accessToken, data.refreshToken)
    await loadMe()
  }

  async function register(input: RegisterInput) {
    await api.post('/auth/register', input)
  }

  async function verifyEmail(token: string) {
    const { data } = await api.post<TokenPair>('/auth/verify-email', { token })
    setTokens(data.accessToken, data.refreshToken)
    await loadMe()
  }

  async function resendVerification(email: string) {
    await api.post('/auth/resend-verification', { email })
  }

  async function forgotPassword(email: string) {
    await api.post('/auth/forgot-password', { email })
  }

  async function resetPassword(token: string, password: string) {
    await api.post('/auth/reset-password', { token, password })
  }

  async function logout() {
    const rt = storedRefreshToken()
    if (rt) {
      try {
        await api.post('/auth/logout', { refreshToken: rt })
      } catch {
        // best-effort
      }
    }
    clearTokens()
    setUser(null)
  }

  return (
    <Ctx.Provider
      value={{
        user,
        loading,
        login,
        googleAuth,
        register,
        verifyEmail,
        resendVerification,
        forgotPassword,
        resetPassword,
        logout,
        refreshUser: loadMe,
      }}
    >
      {children}
    </Ctx.Provider>
  )
}

export function useAuth(): AuthCtx {
  const c = useContext(Ctx)
  if (!c) throw new Error('useAuth outside AuthProvider')
  return c
}

export function apiErrorMessage(err: unknown, fallback: string): string {
  const res = (
    err as { response?: { status?: number; data?: { message?: unknown } } }
  )?.response
  if (res?.status !== undefined && res.status >= 500) return fallback
  const data = res?.data?.message
  if (Array.isArray(data)) return String(data[0] ?? fallback)
  if (typeof data === 'string' && data.length > 0) return data
  return fallback
}
